<template>
    <div
        class="participant-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="participant-modal-title"
        @click.self="closeModal"
    >
        <div class="participant-modal__dialog">
            <header class="participant-modal__header">
                <h3
                    id="participant-modal-title"
                    class="participant-modal__title"
                >
                    Add Group Participants
                </h3>

                <button
                    type="button"
                    class="participant-modal__close"
                    aria-label="Close add participants popup"
                    :disabled="isSubmitting"
                    @click="closeModal"
                >
                    <i
                        class="fa fa-times"
                        aria-hidden="true"
                    ></i>
                </button>
            </header>

            <div class="participant-modal__body">
                <div
                    v-if="isLoading"
                    class="participant-modal__loading"
                    role="status"
                >
                    <i
                        class="fa fa-spinner fa-spin"
                        aria-hidden="true"
                    ></i>

                    <span>Loading participants...</span>
                </div>

                <div
                    v-else-if="loadError"
                    class="participant-modal__error"
                    role="alert"
                >
                    <i
                        class="fa fa-exclamation-circle"
                        aria-hidden="true"
                    ></i>

                    <span>{{ loadError }}</span>

                    <button
                        type="button"
                        class="participant-modal__retry"
                        @click="loadParticipantData"
                    >
                        Retry
                    </button>
                </div>

                <form
                    v-else
                    class="participant-form"
                    @submit.prevent="submitParticipants"
                >
                    <div class="participant-form__field">
                        <label class="participant-form__label">
                            Members
                        </label>

                        <multiselect
                            v-model="selectedUsers"
                            class="participant-form__select"
                            placeholder="Add group participants"
                            track-by="id"
                            :options="availableUsers"
                            :custom-label="getUserLabel"
                            :multiple="true"
                            :taggable="false"
                            :searchable="true"
                            :hide-selected="true"
                            :close-on-select="false"
                            :clear-on-select="false"
                            :allow-empty="true"
                            :disabled="isSubmitting"
                            :max-height="355"
                        >
                            <template
                                slot="option"
                                slot-scope="{ option }"
                            >
                                <div class="participant-option">
                                    <span class="participant-option__name">
                                        {{ getUserFullName(option) }}
                                    </span>

                                    <span
                                        v-if="option.email"
                                        class="participant-option__email"
                                    >
                                        - {{ option.email }}
                                    </span>
                                </div>
                            </template>

                            <template
                                slot="tag"
                                slot-scope="{ option, remove }"
                            >
                                <span class="participant-tag">
                                    <span class="participant-tag__text">
                                        {{ getUserLabel(option) }}
                                    </span>

                                    <button
                                        type="button"
                                        class="participant-tag__remove"
                                        :aria-label="
                                            `Remove ${getUserLabel(option)}`
                                        "
                                        @click.prevent="remove(option)"
                                    >
                                        ×
                                    </button>
                                </span>
                            </template>

                            <span slot="noResult">
                                No matching members found.
                            </span>

                            <span slot="noOptions">
                                No members are available.
                            </span>
                        </multiselect>

                        <small
                            v-if="availableUsers.length === 0"
                            class="participant-form__message"
                        >
                            All available members are already in this group.
                        </small>

                        <small
                            v-else
                            class="participant-form__message"
                        >
                            {{ selectedParticipantText }}
                        </small>
                    </div>

                    <div class="participant-form__actions">
                        <button
                            type="button"
                            class="
                                participant-form__button
                                participant-form__button--cancel
                            "
                            :disabled="isSubmitting"
                            @click="closeModal"
                        >
                            Cancel
                        </button>

                        <button
                            type="submit"
                            class="
                                participant-form__button
                                participant-form__button--submit
                            "
                            :disabled="isSubmitDisabled"
                        >
                            <i
                                v-if="isSubmitting"
                                class="fa fa-spinner fa-spin"
                                aria-hidden="true"
                            ></i>

                            <span>
                                {{
                                    isSubmitting
                                        ? 'Adding...'
                                        : 'Add Participants'
                                }}
                            </span>
                        </button>
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>

<script>
import Multiselect from 'vue-multiselect';
import { bus } from '@/main';
import toast from '@/services/toast';

const BODY_SCROLL_CLASS = 'body_right_scroll_remove';

export default {
    name: 'AddGroupParticipants',

    components: {
        Multiselect
    },

    props: {
        updateGroupInfoID: {
            type: [String, Number],
            required: true
        }
    },

    data() {
        return {
            allUsers: [],
            existingUserIds: [],
            selectedUsers: [],
            isLoading: false,
            isSubmitting: false,
            loadError: ''
        };
    },

    computed: {
        availableUsers() {
            return this.allUsers.filter((user) => {
                return !this.existingUserIds.includes(
                    String(user.id)
                );
            });
        },

        selectedUserIds() {
            return this.selectedUsers.map((user) => user.id);
        },

        isSubmitDisabled() {
            return (
                this.isLoading ||
                this.isSubmitting ||
                this.selectedUserIds.length === 0
            );
        },

        selectedParticipantText() {
            const count = this.selectedUsers.length;

            if (count === 0) {
                return 'No participants selected.';
            }

            if (count === 1) {
                return '1 participant selected.';
            }

            return `${count} participants selected.`;
        }
    },

    mounted() {
        this.addGlobalListeners();
        this.loadParticipantData();
    },

    beforeDestroy() {
        this.removeGlobalListeners();
    },

    methods: {
        async loadParticipantData() {
            if (!this.updateGroupInfoID) {
                this.loadError =
                    'Group information is unavailable.';

                return;
            }

            this.isLoading = true;
            this.loadError = '';

            try {
                const responses = await Promise.all([
                    this.$store.dispatch(
                        'isConditionalListOfAllUsersFun'
                    ),
                    this.$store.dispatch(
                        'fetchGroupChatDetailsById',
                        {
                            id: this.updateGroupInfoID
                        }
                    )
                ]);

                const users = this.extractResponseData(
                    responses[0]
                );

                const groupInfo = this.extractResponseData(
                    responses[1]
                );

                if (!Array.isArray(users)) {
                    throw new Error(
                        'The members list could not be loaded.'
                    );
                }

                if (!groupInfo) {
                    throw new Error(
                        'The group information could not be loaded.'
                    );
                }

                this.allUsers = users
                    .filter((user) => {
                        return (
                            user &&
                            user.id !== undefined &&
                            user.id !== null
                        );
                    })
                    .map((user) => {
                        return this.normalizeUser(user);
                    });

                this.existingUserIds =
                    this.extractExistingUserIds(groupInfo);

                this.selectedUsers =
                    this.selectedUsers.filter((user) => {
                        return !this.existingUserIds.includes(
                            String(user.id)
                        );
                    });
            } catch (error) {
                this.loadError = this.getErrorMessage(
                    error,
                    'Unable to load participants. Please try again.'
                );
            } finally {
                this.isLoading = false;
            }
        },

        async submitParticipants() {
            if (this.isSubmitDisabled) {
                return;
            }

            this.isSubmitting = true;

            try {
                const response = await this.$store.dispatch(
                    'updateGroupChatDetails',
                    {
                        data: {
                            id: this.updateGroupInfoID,
                            data: {
                                users: this.selectedUserIds
                            }
                        }
                    }
                );

                toast.success(
                    this.getSuccessMessage(
                        response,
                        'Participants added successfully.'
                    )
                );

                await this.refreshGroupInformation();
                await this.refreshChatRoomLists();

                this.closeModal(true);
            } catch (error) {
                toast.error(
                    this.getErrorMessage(
                        error,
                        'Unable to add participants. Please try again.'
                    )
                );
            } finally {
                this.isSubmitting = false;
            }
        },

        async refreshGroupInformation() {
            try {
                const response = await this.$store.dispatch(
                    'fetchGroupChatDetailsById',
                    {
                        id: this.updateGroupInfoID
                    }
                );

                const groupInfo =
                    this.extractResponseData(response);

                if (groupInfo) {
                    bus.$emit(
                        'updateGroupInfoDataBus',
                        groupInfo
                    );
                }
            } catch (error) {
                console.error(
                    'Unable to refresh group information:',
                    error
                );
            }
        },

        async refreshChatRoomLists() {
            try {
                await Promise.all([
                    this.$store.dispatch(
                        'fetchPinnedChatRooms'
                    ),
                    this.$store.dispatch(
                        'fetchNormalChatRooms'
                    )
                ]);
            } catch (error) {
                console.error(
                    'Unable to refresh chat room lists:',
                    error
                );
            }
        },

        extractExistingUserIds(groupInfo) {
            if (!Array.isArray(groupInfo.users)) {
                return [];
            }

            return groupInfo.users
                .filter((groupUser) => {
                    return (
                        groupUser &&
                        groupUser.user &&
                        groupUser.user.id !== undefined &&
                        groupUser.user.id !== null
                    );
                })
                .map((groupUser) => {
                    return String(groupUser.user.id);
                });
        },

        normalizeUser(user) {
            const profile = user.profile || {};

            return {
                id: user.id,
                first_name: user.first_name || '',
                last_name: user.last_name || '',
                email: user.email || '',
                thumbnail:
                    profile.thumbnail ||
                    user.thumbnail ||
                    ''
            };
        },

        getUserLabel(user) {
            if (!user) {
                return '';
            }

            const fullName = this.getUserFullName(user);

            if (fullName && user.email) {
                return `${fullName} - ${user.email}`;
            }

            return (
                fullName ||
                user.email ||
                'Unknown member'
            );
        },

        getUserFullName(user) {
            if (!user) {
                return '';
            }

            const fullName = [
                user.first_name || '',
                user.last_name || ''
            ]
                .join(' ')
                .trim();

            return (
                fullName ||
                user.email ||
                'Unknown member'
            );
        },

        extractResponseData(response) {
            if (
                response &&
                response.data &&
                response.data.data !== undefined
            ) {
                return response.data.data;
            }

            return null;
        },

        getSuccessMessage(response, fallbackMessage) {
            if (response && response.data) {
                return (
                    response.data.msg ||
                    response.data.message ||
                    fallbackMessage
                );
            }

            return fallbackMessage;
        },

        getErrorMessage(error, fallbackMessage) {
            if (
                error &&
                error.response &&
                error.response.data
            ) {
                return (
                    error.response.data.msg ||
                    error.response.data.message ||
                    fallbackMessage
                );
            }

            if (error && error.data) {
                return (
                    error.data.msg ||
                    error.data.message ||
                    fallbackMessage
                );
            }

            if (error && error.message) {
                return error.message;
            }

            return fallbackMessage;
        },

        addGlobalListeners() {
            document.body.classList.add(
                BODY_SCROLL_CLASS
            );

            document.addEventListener(
                'keydown',
                this.handleKeydown
            );
        },

        removeGlobalListeners() {
            document.body.classList.remove(
                BODY_SCROLL_CLASS
            );

            document.removeEventListener(
                'keydown',
                this.handleKeydown
            );
        },

        handleKeydown(event) {
            if (event.key === 'Escape') {
                this.closeModal();
            }
        },

        closeModal(forceClose = false) {
            if (this.isSubmitting && !forceClose) {
                return;
            }

            this.removeGlobalListeners();

            bus.$emit(
                'updateGroupInfoPopupStateBus',
                false
            );
        }
    }
};
</script>

<style lang="scss" scoped>
$modal-overlay: rgba(0, 0, 0, 0.92);
$modal-background: #000000;
$modal-border: #ffd400;

$text-primary: #ffffff;
$text-secondary: #aaaaaa;

$dropdown-background: #242424;
$dropdown-header-background: #1b1b1d;
$dropdown-option-text: #ffd400;
$dropdown-active-background: #87cefa;
$dropdown-active-text: #111111;

$danger-color: #ff3a30;
$error-color: #ff5b55;
$border-color: rgba(255, 255, 255, 0.2);

.participant-modal {
    position: fixed;
    inset: 0;
    z-index: 10000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 15px;
    overflow-y: auto;
    color: $text-primary;
    background-color: $modal-overlay;
    font-family: 'MyriadProRegular', 'Lato', sans-serif;

    &__dialog {
        position: relative;
        z-index: 1;
        width: 100%;
        max-width: 640px;
        margin: auto;
        overflow: visible;
        // border: 1px solid $modal-border;
        border-radius: 5px;
        background-color: $modal-background;
        box-shadow: 0 0 13px rgba(255, 213, 0, 0.6);
    }

    &__header {
        position: relative;
        display: flex;
        align-items: center;
        min-height: 60px;
        padding: 15px 55px 15px 15px;
        border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        border-radius: 5px 5px 0 0;
        // background-color: $dropdown-header-background;
    }

    &__title {
        margin: 0;
        color: $text-primary;
        font-size: 20px;
        // font-weight: 600;
        line-height: 1.3;
    }

    &__close {
        position: absolute;
        top: 50%;
        right: 15px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        padding: 0;
        border: 0;
        border-radius: 3px;
        color: $danger-color;
        background: transparent;
        cursor: pointer;
        transform: translateY(-50%);
        transition:
            color 0.2s ease,
            background-color 0.2s ease,
            transform 0.2s ease,
            opacity 0.2s ease;

        .fa {
            font-size: 20px;
        }

        &:hover:not(:disabled),
        &:focus-visible:not(:disabled) {
            color: $dropdown-active-background;
            background-color: rgba(255, 255, 255, 0.08);
            transform: translateY(-50%) scale(1.08);
        }

        &:focus-visible {
            outline: 2px solid $modal-border;
            outline-offset: 2px;
        }

        &:disabled {
            cursor: not-allowed;
            opacity: 0.5;
        }
    }

    &__body {
        padding: 15px;
        overflow: visible;
    }

    &__loading {
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: 120px;
        color: $text-secondary;
        font-size: 16px;

        .fa {
            margin-right: 8px;
            color: $modal-border;
        }
    }

    &__error {
        display: flex;
        flex-direction: column;
        align-items: center;
        min-height: 120px;
        padding: 20px;
        color: $error-color;
        font-size: 15px;
        text-align: center;

        .fa {
            margin-bottom: 8px;
            font-size: 24px;
        }
    }

    &__retry {
        min-width: 90px;
        margin-top: 15px;
        padding: 7px 14px;
        border: 0;
        border-radius: 3px;
        color: $dropdown-active-text;
        background-color: $dropdown-active-background;
        cursor: pointer;

        &:hover,
        &:focus-visible {
            background-color: $modal-border;
        }
    }
}

.participant-form {
    overflow: visible;

    &__field {
        position: relative;
        margin-bottom: 20px;
        overflow: visible;
    }

    &__label {
        display: block;
        margin-bottom: 6px;
        color: $text-primary;
        font-size: 16px;
        line-height: 1.3;
    }

    &__select {
        position: relative;
        z-index: 10;
        width: 100%;
        color: $text-primary;
    }

    &__message {
        display: block;
        margin-top: 7px;
        color: $text-secondary;
        font-size: 13px;
        line-height: 1.3;
    }

    &__actions {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 10px;
    }

    &__button {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 120px;
        min-height: 40px;
        padding: 8px 15px;
        border: 0;
        border-radius: 3px;
        color: $text-primary;
        font-family: inherit;
        font-size: 16px;
        line-height: 1;
        cursor: pointer;
        box-shadow: none;
        transition:
            color 0.2s ease,
            background-color 0.2s ease,
            opacity 0.2s ease;

        .fa {
            margin-right: 6px;
        }

        &:focus-visible {
            outline: 2px solid $modal-border;
            outline-offset: 2px;
        }

        &:disabled {
            cursor: not-allowed;
            opacity: 0.55;
        }

        &--cancel {
            background-color: rgba(255, 255, 255, 0.12);

            &:hover:not(:disabled),
            &:focus-visible:not(:disabled) {
                background-color: rgba(255, 255, 255, 0.22);
            }
        }

        &--submit {
            color: $dropdown-active-text;
            background-color: $dropdown-active-background;

            &:hover:not(:disabled),
            &:focus-visible:not(:disabled) {
                background-color: $modal-border;
            }
        }
    }
}

.participant-option {
    display: flex;
    align-items: center;
    min-width: 0;
    padding:0px;
    font-size: 16px;
    line-height: 1.35;

    &__name {
        flex-shrink: 0;
        color: inherit;
        font-weight: 600;
    }

    &__email {
        min-width: 0;
        margin-left: 4px;
        overflow: hidden;
        color: inherit;
        font-weight: 500;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
}

.participant-tag {
    display: inline-flex;
    align-items: center;
    max-width: 100%;
    margin: 2px 5px 2px 0;
    padding: 5px 7px 5px 9px;
    border-radius: 3px;
    color: #ffffff;
    background-color: #41b883;
    font-size: 16px;
    line-height: 1.2;

    &__text {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    &__remove {
        flex: 0 0 auto;
        margin-left: 7px;
        padding: 0;
        border: 0;
        color: $dropdown-active-text;
        background: transparent;
        font-size: 18px;
        line-height: 1;
        cursor: pointer;

        &:hover,
        &:focus-visible {
            color: #ff0000;
        }
    }
}

/*
 * Use /deep/ for older Vue 2 and vue-loader projects.
 * Replace /deep/ with ::v-deep if your build supports it.
 */
.participant-form__select /deep/ {
    .multiselect {
        min-height: 56px;
        color: $text-primary;
    }

    .multiselect__select {
        top: 0;
        right: 0;
        width: 48px;
        height: 56px;

        &::before {
            top: 54%;
            border-width: 6px 6px 0;
            border-color: #999999 transparent transparent;
        }
    }

    .multiselect--active {
        .multiselect__select {
            transform: rotateZ(180deg);
        }
    }

    .multiselect__tags {
        min-height: 48px;
        padding:10px 48px 8px 16px;
        border:1px solid #212121;
        border-radius: 5px;
        color: $text-primary;
        background-color: $dropdown-header-background;
        font-size: 16px;
        box-shadow: none;
    }

    .multiselect--active {
        .multiselect__tags {
            border-radius: 5px 5px 0 0;
        }
    }

    .multiselect__input,
    .multiselect__single {
        width: 100%;
        margin: 0;
        padding: 0;
        border: 0;
        color: $text-primary;
        background-color: transparent;
        font-family: inherit;
        font-size: 17px;
        line-height: 24px;
        box-shadow: none;

        &::placeholder {
            color: $text-primary;
            opacity: 1;
        }

        &:focus {
            outline: none;
        }
    }

    .multiselect__placeholder {
        margin: 0;
        padding: 0;
        color: $text-primary;
        font-size: 17px;
        // font-weight: 600;
        line-height: 24px;
    }

    .multiselect__content-wrapper {
        z-index: 100;
        max-height: 355px !important;
        overflow-x: hidden;
        overflow-y: auto;
        border: 1px solid #3d3d3d;
        border-top: 0;
        border-radius: 0 0 4px 4px;
        background-color: $dropdown-background;
        box-shadow: none;
        scrollbar-color: #858585 #f1f1f1;
        scrollbar-width: auto;

        &::-webkit-scrollbar {
            width: 17px;
        }

        &::-webkit-scrollbar-track {
            background-color: #f1f1f1;
        }

        &::-webkit-scrollbar-thumb {
            min-height: 45px;
            border: 3px solid #f1f1f1;
            border-radius: 10px;
            background-color: #858585;
        }

        &::-webkit-scrollbar-thumb:hover {
            background-color: #666666;
        }

        &::-webkit-scrollbar-button {
            width: 17px;
            height: 17px;
            background-color: #f1f1f1;
        }
    }

    .multiselect__content {
        width: 100%;
        min-width: 100%;
        padding: 0;
        background-color: $dropdown-background;
    }

    .multiselect__element {
        display: block;
        width: 100%;
    }

    .multiselect__option {
        display: flex;
        align-items: center;
        padding:5px 170px 5px 15px;
        color: $dropdown-option-text;
        background-color: $dropdown-background;
        white-space: normal;
        transition:
            color 0.15s ease,
            background-color 0.15s ease;

        &::after {
            top: 50%;
            right: 15px;
            color: $dropdown-active-text;
            background-color: transparent;
            font-size: 13px;
            line-height: 1;
            transform: translateY(-50%);
        }
    }

    .multiselect__option--highlight {
        color: $dropdown-active-text;
        background-color: $dropdown-active-background;

        .participant-option__name,
        .participant-option__email {
            color: $dropdown-active-text;
        }

        &::after {
            content: 'Press enter to select';
            color: $dropdown-active-text;
            background-color: transparent;
        }
    }

    .multiselect__option--selected {
        color: $dropdown-option-text;
        background-color: #313131;
        font-weight: 600;

        &::after {
            content: 'Selected';
            color: #bcbcbc;
            background-color: transparent;
        }
    }

    .multiselect__option--selected.multiselect__option--highlight {
        color: $dropdown-active-text;
        background-color: $dropdown-active-background;

        .participant-option__name,
        .participant-option__email {
            color: $dropdown-active-text;
        }

        &::after {
            content: 'Press enter to remove';
            color: $dropdown-active-text;
            background-color: transparent;
        }
    }

    .multiselect__option--disabled {
        color: #777777;
        background-color: #2b2b2b;
        cursor: not-allowed;

        &::after {
            color: #777777;
        }
    }

    .multiselect__spinner {
        top: 1px;
        right: 1px;
        width: 46px;
        height: 52px;
        background-color: $dropdown-header-background;

        &::before,
        &::after {
            border-top-color: $modal-border;
        }
    }
}

@media (max-width: 600px) {
    .participant-modal {
        padding: 10px;

        &__header {
            padding: 12px 50px 12px 12px;
        }

        &__title {
            font-size: 18px;
        }

        &__body {
            padding: 12px;
        }
    }

    .participant-form {
        &__actions {
            flex-direction: column-reverse;
        }

        &__button {
            width: 100%;
        }
    }

    .participant-option {
        display: block;
        font-size: 15px;

        &__name,
        &__email {
            display: inline;
            white-space: normal;
        }
    }

    .participant-form__select /deep/ {
        .multiselect__option {
            min-height: 46px;
            padding: 10px 12px;

            &::after {
                display: none;
            }
        }
    }
}
</style>